import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import SubscribeModal from "@/components/SubscribeModal";

// Hide the user-facing billing surface for now — flip to true to re-enable.
// Backend routes, webhooks, and the admin /admin/billing page are unaffected.
const SHOW_BILLING = true;

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatMoney(cents, currency) {
  if (typeof cents !== "number") return "—";
  const amt = cents / 100;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: (currency || "usd").toUpperCase(),
    }).format(amt);
  } catch {
    return `$${amt.toFixed(2)}`;
  }
}

function StatusPill({ status }) {
  const cls =
    status === "active" || status === "trialing"
      ? "bg-emerald-100 text-emerald-700"
      : status === "past_due"
      ? "bg-amber-100 text-amber-800"
      : status === "canceled"
      ? "bg-neutral-200 text-neutral-700"
      : "bg-neutral-100 text-neutral-600";
  const label = status
    ? status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : "None";
  return (
    <span className={`inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full ${cls}`}>
      {label}
    </span>
  );
}

export default function Settings() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [billing, setBilling] = useState(null);
  const [billingLoading, setBillingLoading] = useState(true);
  const [billingErr, setBillingErr] = useState("");
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [pendingActivation, setPendingActivation] = useState(false);

  const loadBilling = useCallback(async () => {
    setBillingLoading(true);
    setBillingErr("");
    try {
      const { data } = await api.get("/billing/me");
      setBilling(data);
      return data;
    } catch (e) {
      setBillingErr(e?.response?.data?.detail || e.message || "Could not load billing");
      return null;
    } finally {
      setBillingLoading(false);
    }
  }, []);

  // Poll /billing/me until the webhook flips the status, or give up after ~15s.
  // Card was already charged client-side; this just waits for our DB to catch up.
  const pollUntilActivated = useCallback(async () => {
    setPendingActivation(true);
    const deadline = Date.now() + 15000;
    try {
      while (Date.now() < deadline) {
        const data = await loadBilling();
        const s = data?.subscription_status;
        if (s === "active" || s === "trialing" || s === "past_due") return;
        await new Promise((r) => setTimeout(r, 1500));
      }
    } finally {
      setPendingActivation(false);
    }
  }, [loadBilling]);

  useEffect(() => {
    if (SHOW_BILLING) loadBilling();
  }, [loadBilling]);

  const handleCancel = async () => {
    if (!window.confirm("Cancel your subscription? You'll keep access until the end of the current period.")) return;
    setCanceling(true);
    try {
      await api.post("/billing/cancel");
      await loadBilling();
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not cancel subscription.");
    } finally {
      setCanceling(false);
    }
  };

  if (!user) return null;

  const status = billing?.subscription_status || "none";
  const sub = billing?.subscription;
  const invoices = billing?.invoices || [];
  const periodEnd = sub?.current_period_end;
  const cancelAtPeriodEnd = !!sub?.cancel_at_period_end;

  return (
    <div className="min-h-screen pb-24 fade-in" data-testid="settings-page">
      <div className="max-w-[820px] mx-auto px-6 pt-8">
        <button
          onClick={() => navigate("/dashboard")}
          className="text-[13px] text-neutral-600 hover:text-black inline-flex items-center gap-1 mb-6"
          data-testid="back-to-dashboard"
        >
          <ChevronLeft size={14} /> Back to dashboard
        </button>

        <div className="section-eyebrow">Settings</div>
        <h1 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Account & integrations</h1>

        {/* PROFILE */}
        <section className="card p-6 mt-6">
          <h2 className="font-display text-lg font-bold tracking-tight mb-4">Profile</h2>
          <div className="flex items-center gap-4">
            {user.picture ? (
              <img src={user.picture} className="w-12 h-12 rounded-full border border-black/5" alt="" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-black text-white flex items-center justify-center font-bold">
                {(user.name || user.email)[0]?.toUpperCase()}
              </div>
            )}
            <div>
              <div className="font-semibold text-[15px]">{user.name || "—"}</div>
              <div className="text-[13px] text-neutral-600">{user.email}</div>
            </div>
          </div>
        </section>


        {/* BILLING */}
        {SHOW_BILLING && (
        <section className="card p-6 mt-4" data-testid="billing-section">
          <div className="flex items-center justify-between mb-1">
            <h2 className="font-display text-lg font-bold tracking-tight">Billing</h2>
            <StatusPill status={status} />
          </div>

          {billingLoading && (
            <div className="text-[13px] text-neutral-600 mt-3">Loading…</div>
          )}
          {billingErr && (
            <div className="text-[13px] text-red-600 mt-3">{billingErr}</div>
          )}

          {!billingLoading && !billingErr && (
            <>
              {pendingActivation && status !== "active" && status !== "trialing" && (
                <div className="mt-3 flex items-center gap-2 text-[13px] text-neutral-700">
                  <span className="inline-block w-3 h-3 rounded-full border-2 border-neutral-300 border-t-neutral-700 animate-spin" />
                  Activating subscription… This usually takes a few seconds.
                </div>
              )}

              {!pendingActivation && (status === "none" ||
                status === "canceled" ||
                status.startsWith("incomplete") ||
                status === "unpaid") && (
                <div className="mt-3">
                  <p className="text-[13px] text-neutral-600 mb-4">
                    You don't have an active subscription.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSubscribeOpen(true)}
                    className="btn-primary text-[13px] py-1.5 px-4"
                    data-testid="subscribe-button"
                  >
                    Subscribe
                  </button>
                </div>
              )}

              {(status === "active" || status === "trialing") && (
                <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="text-[13px] text-neutral-500 mb-0.5">
                      {cancelAtPeriodEnd ? "Access until" : "Next billing date"}
                    </div>
                    <div className="text-[15px] font-semibold text-neutral-900">
                      {formatDate(periodEnd)}
                    </div>
                  </div>
                  {!cancelAtPeriodEnd && (
                    <button
                      type="button"
                      onClick={handleCancel}
                      disabled={canceling}
                      className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-red-700 border border-red-300 bg-white hover:bg-red-50 active:bg-red-100 rounded-md py-1.5 px-4 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="cancel-subscription"
                    >
                      {canceling ? "Canceling…" : "Cancel subscription"}
                    </button>
                  )}
                  {cancelAtPeriodEnd && (
                    <button
                      type="button"
                      onClick={() => setSubscribeOpen(true)}
                      className="btn-primary text-[13px] py-1.5 px-4"
                      data-testid="resubscribe-button"
                    >
                      Reactivate
                    </button>
                  )}
                </div>
              )}

              {status === "past_due" && (
                <div className="mt-3">
                  <div className="text-[13px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                    Your last payment failed. Please contact support to update your card.
                  </div>
                </div>
              )}

              {status === "canceled" && (
                <div className="mt-3">
                  <p className="text-[13px] text-neutral-700 mb-3">
                    Your subscription is canceled.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSubscribeOpen(true)}
                    className="btn-primary text-[13px] py-1.5 px-4"
                    data-testid="resubscribe-button"
                  >
                    Reactivate
                  </button>
                </div>
              )}

              {invoices.length > 0 && (
                <div className="mt-6 pt-5 border-t border-black/5">
                  <div className="text-[12px] font-semibold text-neutral-500 uppercase tracking-wider mb-3">
                    Payment history
                  </div>
                  <table className="w-full text-[13px]">
                    <thead>
                      <tr className="text-left text-neutral-500 border-b border-black/5">
                        <th className="py-1.5 font-medium">Date</th>
                        <th className="py-1.5 font-medium">Amount</th>
                        <th className="py-1.5 font-medium">Status</th>
                        <th className="py-1.5 font-medium text-right">Invoice</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((inv) => (
                        <tr key={inv.stripe_invoice_id} className="border-b border-black/5 last:border-0">
                          <td className="py-1.5">{formatDate(inv.paid_at || inv.created_at)}</td>
                          <td className="py-1.5">{formatMoney(inv.amount_cents, inv.currency)}</td>
                          <td className="py-1.5">
                            <StatusPill status={inv.status} />
                          </td>
                          <td className="py-1.5 text-right">
                            {inv.hosted_invoice_url ? (
                              <a
                                href={inv.hosted_invoice_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[12px] text-neutral-600 hover:text-black inline-flex items-center gap-1"
                              >
                                View <ExternalLink size={11} />
                              </a>
                            ) : (
                              <span className="text-neutral-400">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </section>
        )}
      </div>

      {SHOW_BILLING && (
        <SubscribeModal
          open={subscribeOpen}
          onClose={() => setSubscribeOpen(false)}
          onSuccess={async () => {
            setSubscribeOpen(false);
            // Webhook may lag a few seconds; poll until the DB flips to active
            // and show an "Activating…" notice in the meantime so the user
            // doesn't briefly see "Not subscribed".
            await pollUntilActivated();
          }}
        />
      )}
    </div>
  );
}
